import { useMemo, useState } from 'react';
import addDays from 'date-fns/addDays';
import subDays from 'date-fns/subDays';
import '../../../../../packages/utils/src/registerDatepickerLocales';

// Components
import { Filters, ConfigContext, TextBlock, SizeProvider } from '@velinsi/smp-widgets-ui';
import EventRowWithCompetition from '../../components/EventSection/EventRowWithCompetition';

// Providers
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';

// Types
import { BaseWidgetProps, INormalizedTennisMatch, ITennisLivescoreConfig, ITournament } from '../../../../../packages/types';

// Misc
import { normalizeTennisTournamentResponseData } from '../../normalizers/normalizeTennisTournamentResponseData';

// Constants
import { ALL, statusFilters } from '../../../../../packages/constants/statusFilters';
import { Sports } from '../../../../../packages/constants/sportType';

// Hooks
import useLivescore from '../../hooks/useLivescore';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import datePickerStyles from 'react-datepicker/dist/react-datepicker.min.css';
import { defaultHandleCompetitionClick, defaultHandleEventClick, extractOnlyDateSDKOptions } from '@sportal365/smp-widget-utils';
import { useIsSmartphoneDevice } from '../../../../../packages/hooks/useIsSmartphoneDevice';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import dayjs from 'dayjs';

export type LivescoreWidgetProps = BaseWidgetProps<ITennisLivescoreConfig>;

export const LivescoreWidget = ({ themes, config, subscribers, sdkOptions, shadow, cuid }: LivescoreWidgetProps) => {
  const showHeader = config?.dataHeaderDisplay === null || config?.dataHeaderDisplay === true;
  const date = dayjs(config?.dataDate?.date, config?.dataDate?.dateFormat).toDate();

  const [selectedDate, setSelectedDate] = useState<Date>(date);
  const [selectedFilter, setSelectedFilter] = useState<string>(config?.dataHeaderDefaultOption ? config?.dataHeaderDefaultOption : ALL);
  const { ref } = useRootNodeRef();
  const { isMobile } = useIsSmartphoneDevice(ref);

  const selectDate = (date: Date) => setSelectedDate(date);
  const addDay = () => setSelectedDate(addDays(selectedDate, 1));
  const subtractDay = () => setSelectedDate(subDays(selectedDate, 1));
  const selectFilter = (filter: string) => setSelectedFilter(filter);

  const {
    showWidget,
    handleClick,
    widgetData: { livescore, isLoading },
  } = useLivescore({
    subscribers,
    dataRefreshTime: config?.dataRefreshTime,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    statusFilter: selectedFilter,
    dateFilter: selectedDate,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsMarket: config?.dataOddsMarket,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataPopularList: config?.dataPopularList,
    dataWidgetCuid: cuid,
  });

  const data = useMemo(
    () =>
      normalizeTennisTournamentResponseData({
        livescore,
        dataEntityLinks: config?.dataEntityLinks,
        sdkDateOptions: extractOnlyDateSDKOptions(sdkOptions),
      }),
    [livescore, config, sdkOptions],
  );

  if (!showWidget) return null;

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, event);

  const handleCompetitionClick = (competition: ITournament) => defaultHandleCompetitionClick(handleClick, competition);

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <div ref={ref}>
        <ThemeProvider
          isLoading={isLoading}
          datePickerStyles={datePickerStyles}
          shadow={shadow}
          themes={themes}
          dataTheme={config?.dataTheme}
          css={{ backgroundColor: '$primaryBackgroundColor' }}
        >
          <SizeProvider>
            <Filters
              statusFilters={statusFilters}
              dataDate={config?.dataDate}
              dataLabels={config?.dataLabels}
              language={sdkOptions?.dataConfigLang}
              dataPopularList={config?.dataPopularList}
              date={selectedDate}
              filter={selectedFilter}
              handleDateSelect={selectDate}
              handleNextArrowClick={addDay}
              handleFilterClick={selectFilter}
              handlePrevArrowClick={subtractDay}
              handleClick={handleClick}
              theme={config?.dataTheme}
              showHeader={showHeader}
              displayOptions={config?.dataOptionsDisplay}
            />
            {livescore?.length < 1 || livescore === undefined ? (
              <TextBlock text={config?.dataLabels?.no_matches} />
            ) : (
              data?.map(({ competition, matches }) => (
                <EventRowWithCompetition
                  sport={Sports.TENNIS}
                  key={competition?.id}
                  isMobile={isMobile}
                  competition={competition}
                  showOdds={config?.dataOddsDisplay}
                  matches={matches as INormalizedTennisMatch[]}
                  handleClick={handleClick}
                  handleCompetitionClick={handleCompetitionClick}
                  handleEventClick={handleEventClick}
                  allowInlineOdds={config?.dataAllowInlineOdds}
                />
              ))
            )}
          </SizeProvider>
        </ThemeProvider>
      </div>
    </ConfigContext.Provider>
  );
};
